// src/Views/RequestView.js
import React, { useState, useEffect, useContext } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { StoreContext } from '../contexts/StoreContext';
import { MAX_CONTENT_WIDTH, BORDER_RADIUS, SPACING_SECTION, SPACING_BUTTON, SPACING_CARD, SPACING_CARD_BODY, SPACING_COMPONENT, SPACING_ELEMENT, SPACING_GROUP } from '../constants/layout';
import {
  FONT_TITLE2,
  FONT_BODY,
} from '../constants/typography';

export default function RequestView() {
  const { store } = useContext(StoreContext);
  const phoneNumber = store?.storeContact?.phoneNumber?.trim() || '';
  const phoneHref = phoneNumber ? `tel:${phoneNumber.replace(/[^+\d]/g, '')}` : '';
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    document.title = 'Reach Out';
  }, []);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await addDoc(collection(db, 'contactSubmissions'), {
        name: contactName,
        email: contactEmail,
        phone: contactPhone,
        message: contactMessage,
        createdAt: serverTimestamp(),
        isRead: false
      });

      setIsSubmitted(true);
      setContactName('');
      setContactEmail('');
      setContactPhone('');
      setContactMessage('');
    } catch (error) {
      console.error('Error submitting contact form:', error);
      alert('There was an error sending your message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendAnother = () => {
    setIsSubmitted(false);
  };

  return (
    <div className="contact-page">
      <div className="contact-container">
        <div className="contact-wrapper">
          <div className="contact-info">
            <h1 className="contact-heading">Reach Out</h1>
            <p className="contact-description">
              {phoneNumber ? (
                <>Interested in our corals? Contact us at <a href={phoneHref} className="phone-link">{phoneNumber}</a> or send us a message and we'll get back to you.</>
              ) : (
                <>Interested in our corals? Send us a message and we'll get back to you.</>
              )}
            </p>
          </div>

          {isSubmitted ? (
            <div className="thank-you-message">
              <span className="material-icons thank-you-icon">check_circle</span>
              <h2 className="thank-you-title">Thank You!</h2>
              <p className="thank-you-text">Someone will get back to you soon.</p>
              <button
                type="button"
                className="send-another-button"
                onClick={handleSendAnother}
              >
                Send Another Message
              </button>
            </div>
          ) : (
            <form className="contact-form" onSubmit={handleContactSubmit}>
              <input
                type="text"
                placeholder="Your Name *"
                className="form-input"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                required
              />
              <input
                type="email"
                placeholder="Your Email"
                className="form-input"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
              />
              <input
                type="tel"
                placeholder="Your Phone Number *"
                className="form-input"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                required
              />
              <textarea
                placeholder="Message (optional)"
                className="form-input form-textarea"
                rows="4"
                value={contactMessage}
                onChange={(e) => setContactMessage(e.target.value)}
              />
              <button type="submit" className="submit-button" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Submit Request'}
              </button>
            </form>
          )}
        </div>
      </div>

      <style jsx>{`
        .contact-page {
          min-height: calc(100vh - 200px);
          background-color: var(--color-background);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: ${SPACING_SECTION} ${SPACING_BUTTON};
          padding-left: max(${SPACING_BUTTON}, env(safe-area-inset-left));
          padding-right: max(${SPACING_BUTTON}, env(safe-area-inset-right));
        }

        .contact-container {
          max-width: ${MAX_CONTENT_WIDTH};
          margin: 0 auto;
        }

        .contact-wrapper {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: ${SPACING_SECTION};
          max-width: 900px;
          margin: 0 auto;
          align-items: center;
        }

        .contact-info {
          text-align: left;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .contact-heading {
          font-size: ${FONT_TITLE2};
          font-weight: 600;
          color: var(--color-text-primary);
          margin: 0;
          line-height: 1.1;
        }

        .contact-description {
          font-size: ${FONT_BODY};
          color: var(--color-text-secondary);
          margin: ${SPACING_BUTTON} 0 ${SPACING_CARD_BODY} 0;
          line-height: 1.6;
        }

        .phone-link {
          color: var(--color-accent);
          text-decoration: none;
          font-weight: 600;
        }

        .phone-link:hover {
          text-decoration: underline;
        }

        .contact-form {
          display: flex;
          flex-direction: column;
          gap: ${SPACING_BUTTON};
        }

        .form-input {
          padding: ${SPACING_BUTTON};
          font-size: ${FONT_BODY};
          border: 1px solid var(--color-border);
          border-radius: ${BORDER_RADIUS};
          background-color: var(--color-surface);
          color: var(--color-text-primary);
          transition: border-color 0.2s ease;
        }

        .form-input::placeholder {
          color: var(--color-text-muted);
        }

        .form-input:focus {
          outline: none;
          border-color: var(--color-accent);
        }

        .form-textarea {
          resize: vertical;
          min-height: 100px;
          font-family: inherit;
        }

        .submit-button {
          padding: ${SPACING_BUTTON} ${SPACING_GROUP};
          font-size: ${FONT_BODY};
          font-weight: 600;
          background-color: var(--color-accent);
          color: var(--color-accent-text);
          border: none;
          border-radius: ${BORDER_RADIUS};
          cursor: pointer;
          transition: background-color 0.2s ease;
        }

        .submit-button:hover:not(:disabled) {
          background-color: var(--color-accent-hover);
        }

        .submit-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .thank-you-message {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: ${SPACING_SECTION} ${SPACING_CARD_BODY};
          background-color: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: ${BORDER_RADIUS};
        }

        .thank-you-icon {
          font-size: 64px;
          color: #10B981;
          margin-bottom: ${SPACING_BUTTON};
        }

        .thank-you-title {
          font-size: ${FONT_TITLE2};
          font-weight: 600;
          color: var(--color-text-primary);
          margin: 0 0 ${SPACING_ELEMENT} 0;
        }

        .thank-you-text {
          font-size: ${FONT_BODY};
          color: var(--color-text-secondary);
          margin: 0 0 ${SPACING_CARD_BODY} 0;
        }

        .send-another-button {
          padding: ${SPACING_COMPONENT} ${SPACING_CARD_BODY};
          font-size: ${FONT_BODY};
          font-weight: 600;
          background: transparent;
          color: var(--color-accent);
          border: 1px solid var(--color-accent);
          border-radius: ${BORDER_RADIUS};
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .send-another-button:hover {
          background-color: var(--color-accent);
          color: var(--color-accent-text);
        }

        @media (max-width: 768px) {
          .contact-wrapper {
            grid-template-columns: 1fr;
            gap: ${SPACING_SECTION};
          }

          .contact-heading {
            font-size: 1.75rem;
          }

          .contact-info {
            text-align: center;
          }
        }
      `}</style>
    </div>
  );
}
